"use client";

import { forwardRef, InputHTMLAttributes } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  label?: string;
  error?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, className = "", id, ...props }, ref) => {
    const checkboxId = id || label?.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className="flex flex-col gap-1">
        <label
          htmlFor={checkboxId}
          className={cn(
            "group inline-flex items-center gap-3 cursor-pointer select-none",
            props.disabled && "opacity-40 cursor-not-allowed",
          )}
        >
          <div className="relative flex items-center justify-center">
            <input
              type="checkbox"
              ref={ref}
              id={checkboxId}
              className={cn(
                "peer h-5 w-5 appearance-none rounded-md border-2 border-bd-hi bg-input",
                "transition-[background-color,border-color,box-shadow] duration-[220ms]",
                "checked:bg-primary checked:border-primary",
                "hover:[border-color:rgba(0,214,143,0.5)]",
                "focus-ring disabled:cursor-not-allowed",
                error && "border-error",
                className,
              )}
              aria-invalid={error ? "true" : "false"}
              aria-describedby={error ? `${checkboxId}-error` : undefined}
              {...props}
            />
            <Check
              aria-hidden
              strokeWidth={3.5}
              className="absolute w-3 h-3 text-primary-foreground scale-0 opacity-0 transition-[transform,opacity] duration-[220ms] peer-checked:scale-100 peer-checked:opacity-100 pointer-events-none"
            />
          </div>
          {label && (
            <span className="text-sm font-medium text-foreground tracking-[-0.01em]">
              {label}
            </span>
          )}
        </label>
        {error && (
          <p id={`${checkboxId}-error`} className="text-xs text-error ml-8">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";

export { Checkbox, type CheckboxProps };
